import { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import styled from '@emotion/styled';
import { FixedSizeList } from 'react-window';
import { tokens } from '@hotjar/hotsauce';
import { useResizeObserver } from '@hotjar/webapp/hooks/useResizeObserver';
import { EventMarker } from '@hotjar/webapp/features/Playbacks/types';
import { useRealTimeStore } from '@hotjar/webapp/features/Playbacks/store/realTimeStore';

import { ItemRenderer } from './components/ItemRenderer';
import { findClosestItemIndex } from './utils/findClosestItemIndex';
import { getOffsetForIndex } from './utils/getOffsetForIndex';
import { filterActions } from './utils/filterActions';
import { ACTION_FILTERS, ITEM_SIZE } from './constants';
import { InnerElement } from './components/InnerElement';
import { ActionsFilter } from './components/ActionsFilter';
import type { ActionFilter } from './types';
import { EmptyFiltersFeedback } from '@hotjar/recordings/src/features/PlayerPage/components/EventBoxHolder/components/EmptyFiltersFeedback';

const ContainerDiv = styled.div`
  flex: 1 1 0;
  overflow: hidden;
  background-color: ${tokens.color.neutral_100};
`;

export interface Props {
  sidePanelEventMarkers: EventMarker[];
  actionFilters: ActionFilter[] | undefined;
  onActionFilterChange: (filters: ActionFilter[] | undefined) => void;
}

export const EventBoxHolder = ({
  sidePanelEventMarkers = [],
  actionFilters,
  onActionFilterChange,
}: Props): JSX.Element | null => {
  const currentTimeWithLessPrecision = useRealTimeStore(
    (state) => state.currentTimeWithLessPrecision,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<FixedSizeList>(null);
  const [lastScrollItemIndex, setLastScrollItemIndex] = useState(-1);
  const { width, height } = useResizeObserver(containerRef);

  const filteredActions = useMemo(
    () => filterActions(sidePanelEventMarkers, actionFilters),
    [sidePanelEventMarkers, actionFilters],
  );

  useEffect(() => {
    if (!listRef.current) return;

    const indexOfTheItem = findClosestItemIndex(currentTimeWithLessPrecision, filteredActions);
    if (indexOfTheItem === lastScrollItemIndex) {
      return;
    }

    setLastScrollItemIndex(indexOfTheItem);
    listRef.current.scrollTo(getOffsetForIndex(indexOfTheItem, filteredActions.length, height));
  }, [currentTimeWithLessPrecision, filteredActions, height, lastScrollItemIndex, listRef]);

  const resetFilters = useCallback(() => {
    onActionFilterChange(Object.keys(ACTION_FILTERS) as ActionFilter[]);
  }, [onActionFilterChange]);

  return (
    <>
      <ActionsFilter filters={actionFilters} onFilterChange={onActionFilterChange} />
      {!filteredActions.length && actionFilters?.length ? (
        <EmptyFiltersFeedback
          totalActions={sidePanelEventMarkers.length}
          resetFilters={resetFilters}
        />
      ) : null}
      <ContainerDiv className="events" ref={containerRef}>
        <FixedSizeList
          ref={listRef}
          itemData={filteredActions}
          itemCount={filteredActions.length}
          itemSize={ITEM_SIZE}
          innerElementType={InnerElement}
          height={height}
          width={width}
        >
          {ItemRenderer}
        </FixedSizeList>
      </ContainerDiv>
    </>
  );
};
